<template>

  <v-container fluid class="mb-6 page-content order-list-table">

    <!----------------------------->
    <!-- Page Label and Controls -->
    <!----------------------------->
    <v-container fluid class="pa-0">
      <v-row no-gutters>
        <v-col cols="12" xs="12" sm="6" md="6" lg="6">
          <span class="mr-3 page-label">{{ title }}</span>
          <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="info-timezone" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
            </template>
            <div>Dates & times are: {{ activeTimezone }}</div>
          </v-tooltip>
        </v-col>
        <v-col cols="12" xs="12" sm="6" md="6" lg="6" class="d-flex align-start justify-center justify-sm-end">
          <!-- (toggle confirm codes) -->
          <template v-if="allowRevealConfirmCode">
            <v-btn @click="revealConfirmCodes = !revealConfirmCodes">
              <span v-if="revealConfirmCodes">Hide Confirm Codes</span>
              <span v-else>Show Confirm Codes</span>
            </v-btn>
            <v-icon class="ml-3" style="padding-top:6px;" color="primary" v-if="revealConfirmCodes">mdi-eye</v-icon>
            <v-icon class="ml-3" style="padding-top:6px;" color="white" v-else>mdi-eye-off</v-icon>
          </template>
        </v-col>
      </v-row>

      <v-row>
        <!-- Find by order number -->
        <v-col cols="12" xs="12" sm="12" md="7" lg="5" class="d-flex align-center justify-center justify-sm-start">
          <v-text-field
              clearable
              single-line
              hide-details
              style="max-width:300px;"
              class="ml-2 mb-2"
              label="Order number"
              append-icon="mdi-magnify"
              v-model="search" />
        </v-col>
        <!-- Filter by Live and Test -->
        <v-col cols="12" xs="12" sm="12" md="5" lg="4" class="">
          <div class="page-super-title">Show</div>
          <div class="d-flex align-center">
            <v-checkbox
                class="mt-0"
                :label="(credits) ? 'Live Credits' : 'Live Orders'"
                hide-details
                @change="queryData"
                v-model="showLiveOrders" />
            <v-checkbox
                class="pl-8 mt-0"
                :label="(credits) ? 'Test Credits' : 'Test Orders'"
                hide-details
                @change="queryData"
                v-model="showTestOrders" />
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!---------------->
    <!-- Order List -->
    <!---------------->
    <div class="mt-10">
      <v-data-table
          class="list-orders"
          :headers="headers"
          :items="orderItems"
          :search="search"
          :options.sync="dataOptions"
          :footer-props="paginationOptions"
          :loading="isDataLoading"
          :server-items-length="totalItems"
          :show-select="isSelectable"
          v-model="selected">

        <!-- header: notifications -->
        <!-- eslint-disable-next-line vue/no-unused-vars -->
        <template v-slot:[`header.notifications`]="{ header }">
          <v-icon small color="#c9c9c9">mdi-bell</v-icon>
        </template>

        <!-- notifications -->
        <template v-slot:[`item.notifications`]="{ item }">
          <v-tooltip top v-if="hasNotifications(item)">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-badge
                    bordered
                    style="cursor:pointer;"
                    color="error"
                    offset-x="12"
                    offset-y="12"
                    :content="getNotificationCount(item)">
                  <router-link :to="generateOrderMessagesPageLink(item)">
                    <v-btn icon>
                      <v-icon style="font-size:18px;">mdi-message-text-outline</v-icon>
                    </v-btn>
                  </router-link>
                </v-badge>
              </span>
            </template>
            <div>You have messages from the receiver</div>
          </v-tooltip>
        </template>

        <!-- order number (link to order info page) -->
        <template v-slot:[`item.order_number`]="{ item }">
          <!-- Test Order indicator -->
          <div class="mb-2" v-if="item.is_test">
            <v-chip label color="orange darken-4" style="font-weight:500;">
              <span v-if="item.is_credit">TEST CREDIT</span>
              <span v-else>TEST ORDER</span>
            </v-chip>
          </div>
          <!----------------->
          <!-- Credit Memo -->
          <!----------------->
          <template v-if="item.is_credit">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="yellow darken-4"
                    style="cursor:pointer;font-size:21px;margin-right:3px;padding-bottom:5px;"
                    v-bind="attrs"
                    v-on="on">
                  mdi-credit-card
                </v-icon>
              </template>
              <div style="font-weight:600">Order Credit</div>
            </v-tooltip>
            <span style="font-size:1.2em">
              <router-link :to="generateOrderInfoPageLink(item)">
                {{ item.thirdparty_order_number }}
              </router-link>
            </span>
          </template>
          <!-------------------->
          <!-- Standard Order -->
          <!-------------------->
          <template v-else>
            <!-- (rejected order) -->
            <template v-if="item.rejected_at">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                      color="red darken-4"
                      style="cursor:pointer;font-size:21px;margin-right:3px;padding-bottom:5px;"
                      v-bind="attrs"
                      v-on="on">
                    mdi-alert-box-outline
                  </v-icon>
                </template>
                <div style="font-weight:600" v-if="item.order_status === 'REJECTED_ON_DELIVERY'">Order was rejected on delivery:</div>
                <div style="font-weight:600" v-else>Order was rejected:</div>
                <div v-if="item.receiver_notes" class="mt-1">{{ item.receiver_notes }}</div>
              </v-tooltip>
            </template>
            <!-- (rescheduled order) -->
            <template v-if="item.rescheduled_at">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                      color="orange accent-3"
                      style="cursor:pointer;font-size:20px;margin-right:3px;padding-bottom:5px;"
                      v-bind="attrs"
                      v-on="on">
                    mdi-calendar-refresh
                  </v-icon>
                </template>
                <div style="font-weight:600">Order was rescheduled by dispatcher:</div>
                <div v-if="item.dispatcher_notes && item.dispatcher_notes.reschedule" class="mt-1">
                  {{ item.dispatcher_notes.reschedule }}
                </div>
              </v-tooltip>
            </template>
            <!-- (auto-placed order due to previous delivery rejection or rescheduling) -->
            <template v-if="item.parent_order_id">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                      color="orange accent-3"
                      style="cursor:pointer;font-size:24px;margin-right:3px;padding-bottom:5px;"
                      v-bind="attrs"
                      v-on="on">
                    mdi-repeat-variant
                  </v-icon>
                </template>
                <div style="font-weight:600">(Re-submitted order)</div>
                <!-- due to rejection -->
                <template v-if="item.parent_order && item.parent_order.rejected_at">
                  <div>Last delivery was rejected by receiving agent:</div>
                  <div v-if="item.parent_order.receiver_notes" class="mt-1">{{ item.parent_order.receiver_notes }}</div>
                </template>
                <!-- due to rescheduling -->
                <template v-if="item.parent_order && item.parent_order.rescheduled_at">
                  <div>Last delivery was rescheduled:</div>
                  <div v-if="item.parent_order.dispatcher_notes && item.parent_order.dispatcher_notes.reschedule" class="mt-1">
                    {{ item.parent_order.dispatcher_notes.reschedule }}
                  </div>
                </template>
              </v-tooltip>
            </template>
            <span style="font-size:1.2em">
              <router-link :to="generateOrderInfoPageLink(item)">
                {{ item.thirdparty_order_number }}
              </router-link>
            </span>
          </template>
        </template>

        <!-- order total -->
        <template v-slot:[`item.order_details.total`]="{ item }">
          <span class="order-total">{{ formatTotal(item.order_details) }}</span>
        </template>

        <!-- provider alias -->
        <template v-slot:[`item.provider_alias`]="{ item }">
          <span class="provider-name">{{ formatProvider(item.provider_alias) }}</span>
        </template>

        <!-- provider account -->
        <template v-slot:[`item.thirdparty_account_name`]="{ item }">
          <span>{{ item.thirdparty_account_name }}</span>
        </template>

        <!-- provider brand -->
        <template v-slot:[`item.thirdparty_brand_name`]="{ item }">
          <span>{{ item.thirdparty_brand_name }}</span>
        </template>

        <!-- pickup location (shipper) -->
        <template v-slot:[`item.shipper.name`]="{ item }">
          <span v-if="linkToLocations">
            <router-link :to="generateLocationPageLink(item.shipper)">
              {{ formatLocation(item.shipper) }}
            </router-link>
          </span>
          <span v-else>{{ formatLocation(item.shipper) }}</span>
        </template>

        <!-- dropoff location (receiver) -->
        <template v-slot:[`item.receiver.name`]="{ item }">
          <span v-if="linkToLocations">
            <router-link :to="generateLocationPageLink(item.receiver)">
              {{ formatLocation(item.receiver) }}
            </router-link>
          </span>
          <span v-else>{{ formatLocation(item.receiver) }}</span>
        </template>

        <!-- active route -->
        <template v-slot:[`item.route_id`]="{ item }">
          <div class="mb-3 route-name-col-value">
            {{ item.route.name }}
          </div>
          <template v-if="routeClickGoTo === 'delivery-stepper'">
            <v-btn color="primary" @click="handleRouteClicked(item)" v-if="item.route && item.route.route_status === 'DISPATCHED'">
              Start Delivery
            </v-btn>
            <v-btn color="primary" @click="handleRouteClicked(item)" v-else>
              View Delivery
            </v-btn>
          </template>
          <template v-if="routeClickGoTo === 'route-builder'">
            <v-btn color="primary" @click="handleRouteClicked(item)" v-if="item.route && item.route.route_status === 'DISPATCHED'">
              Go to Route
            </v-btn>
            <v-btn color="primary" @click="handleRouteClicked(item)" v-else>
              Finish Route
            </v-btn>
          </template>
        </template>

        <!-- placed date -->
        <template v-slot:[`item.placed_at`]="{ item }">
          <span>{{ formatDateTime(item.placed_at) }}</span>
        </template>

        <!-- expected delivery date -->
        <template v-slot:[`item.expected_delivery`]="{ item }">
          <span>{{ formatDate(item.expected_delivery) }}</span>
        </template>

        <!-- scheduled delivery date -->
        <template v-slot:[`item.scheduled_delivery`]="{ item }">
          <span>{{ formatDate(item.scheduled_delivery) }}</span>
        </template>

        <!-- estimated shipper arrival time -->
        <template v-slot:[`item.estimated_shipper_arrival_time`]="{ item }">
          <span>{{ formatTime(item.estimated_shipper_arrival_time) }}</span>
        </template>

        <!-- estimated receiver arrival time -->
        <template v-slot:[`item.estimated_receiver_arrival_time`]="{ item }">
          <span>{{ formatTime(item.estimated_receiver_arrival_time) }}</span>
        </template>

        <!-- delivered date/time -->
        <template v-slot:[`item.delivered_at`]="{ item }">
          <span>{{ formatDateTime(item.delivered_at) }}</span>
        </template>

        <!-- rescheduled date/time -->
        <template v-slot:[`item.rescheduled_at`]="{ item }">
          <span>{{ formatDateTime(item.rescheduled_at) }}</span>
        </template>

        <!-- rejected date/time -->
        <template v-slot:[`item.rejected_at`]="{ item }">
          <span>{{ formatDateTime(item.rejected_at) }}</span>
        </template>

        <!-- canceled date/time -->
        <template v-slot:[`item.canceled_at`]="{ item }">
          <span>{{ formatDateTime(item.canceled_at) }}</span>
        </template>

        <!-- completed date/time -->
        <template v-slot:[`item.completed_at`]="{ item }">
          <span>{{ formatDateTime(item.completed_at) }}</span>
        </template>

        <!-- confirm code -->
        <template v-slot:[`item.confirm_code`]="{ item }">
          <span class="confirm-code">
            <span v-if="revealConfirmCodes">{{ item.confirm_code.confirmation_code }}</span>
            <span v-else>******</span>
          </span>
        </template>

        <!-- order status -->
        <template v-slot:[`item.order_status`]="{ item }">
          <v-chip label outlined :class="item.order_status">
            {{ formatStatusLabel(item.order_status) }}
          </v-chip>
        </template>
      </v-data-table>
    </div>

  </v-container>

</template>

<script>
/* eslint-disable no-unneeded-ternary */
export default {
  name: 'OrderListDataTable',

  props: {
    headers: {
      type: Array,
      default: () => [
        { text: 'Notifications', value: 'notifications', sortable: false, width: '48px' },
        { text: 'RouteLeaf Order#', value: 'order_number' },
        { text: 'Total', value: 'order_details.total' },
        { text: 'Provider', value: 'provider_alias' },
        { text: 'From', value: 'shipper.name' },
        { text: 'To', value: 'receiver.name' },
        { text: 'Expected Delivery', value: 'expected_delivery', align: 'center' },
        { text: 'Scheduled Delivery', value: 'scheduled_delivery' },
        { text: 'Delivered', value: 'delivered_at' },
        { text: 'Shipment Info', sortable: false, value: 'name' },
        { text: 'Confirm Code', sortable: false, value: 'confirm_code' },
        { text: 'Status', value: 'order_status' }
      ]
    },
    notifications: {
      type: Array,
      default: () => []
    },
    status: {
      type: Array,
      default: () => null
    },
    credits: {
      type: Boolean,
      default: false
    },
    assocs: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: 'Orders'
    },
    isSelectable: {
      type: Boolean,
      default: false
    },
    allowRevealConfirmCode: {
      type: Boolean,
      default: false
    },
    linkToLocations: {
      type: Boolean,
      default: false
    },
    routeClickGoTo: {
      type: String,
      default: null // options = delivery-stepper, route-builder
    },
    defaultSortField: {
      type: String,
      default: 'placed_at'
    },
    defaultSortOrder: {
      type: String,
      default: 'dsc' // dsc = descending, asc = ascending
    },
    shipperDetailsPage: {
      type: String,
      default: null
    },
    receiverDetailsPage: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      isDataLoading: false,
      dataLoadingError: null,
      orderData: null,
      paginationOptions: {
        'items-per-page-options': [10, 25, 50, 100]
      },
      dataOptions: {
        itemsPerPage: 10,
        // NOTE - Currently enabled for single field sort only !!!
        sortBy: [this.defaultSortField],
        sortDesc: (this.defaultSortOrder && this.defaultSortOrder === 'dsc') ? [true] : [false]
      },
      showTestOrders: true,
      showLiveOrders: true,
      search: null,
      selected: [],
      revealConfirmCodes: false
    }
  },

  computed: {
    activeTimezone () {
      return this.$store.getters['app/defaultTimezone']
    },
    orderItems () {
      return (this.orderData) ? this.orderData.items : []
    },
    dataMeta () {
      return (this.orderData) ? this.orderData.meta : {}
    },
    totalItems () {
      return this.dataMeta.total_items || 0
    }
  },

  watch: {
    // Observe changes invoked by the data-table component (e.g. pagination, sorting)
    dataOptions: {
      handler () {
        this.queryData()
      },
      deep: true
    },
    // Observe changes to the search field
    search: function (updated) {
      this.queryData()
    }
  },

  methods: {
    // Display a date
    formatDate (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMM D, YYYY') : '-'
    },

    // Display a time
    formatTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'h:mma') : ''
    },

    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMM D, YYYY h:mma') : ''
    },

    // Display total
    formatTotal (orderDetails = {}) {
      const totalAsNum = (orderDetails.total) ? Number(orderDetails.total) : 0
      return `$${totalAsNum.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    },

    // Display provider name
    formatProvider (alias) {
      return (alias) ? this.$root.providerAliasToName(alias) : ''
    },

    // Display location name
    formatLocation (loc) {
      return (loc && loc.name) ? loc.name : '-'
    },

    // Display status label
    formatStatusLabel (statusCode) {
      return (statusCode === 'REJECTED_ON_DELIVERY') ? 'REJECTED' : statusCode
    },

    // Get notifications for the provided order
    getNotificationCount (order) {
      if (!order || !order.id) return 0
      return (this.notifications && this.notifications.length > 0)
        ? this.notifications.filter(it => it.order_id === order.id).length
        : 0
    },

    // Checks if an order has notifications
    hasNotifications (order) {
      return this.getNotificationCount(order) > 0
    },

    // Direct to route target page per configuration
    handleRouteClicked (item) {
      if (this.routeClickGoTo) {
        switch (this.routeClickGoTo) {
          case 'delivery-stepper': this.goToDeliveryPage(item); break
          case 'route-builder': {
            if (item.route) this.goToRouteBuilderPage(item.route)
            break
          }
        }
      }
    },

    // Go to the active delivery page (delivery stepper)
    goToDeliveryPage (item) {
      if (item && item.model) {
        this.$root.goTo('delivery-stepper', {
          routeId: item.route_id
        })
      } else {
        console.error('Invalid item data. Cannot invoke item page.')
      }
    },

    // Go to the route builder page
    async goToRouteBuilderPage (route) {
      if (route && route.model) {
        // Load route with associations for builder page
        const routeWithAssocs = await this.$store.dispatch('sh/fetchData', {
          resource: 'route',
          id: route.id,
          params: { with: 'orders,vehicle,driver' }
        })

        this.$store.dispatch('sh/setItemToEdit', routeWithAssocs) // set item to edit
        this.$root.goTo('route-builder', { routeId: routeWithAssocs.id }) // go to route builder page
      } else {
        console.error('Invalid item data. Cannot invoke item page.')
      }
    },

    // Generate link data for shipper/receiver page (for a provided location item)
    generateLocationPageLink (loc) {
      if (loc && loc.model) {
        if (loc.model === 'Shipper') {
          return {
            name: 'shipper-item',
            params: { shipperId: loc.id }
          }
        } else if (loc.model === 'Receiver') {
          return {
            name: 'receiver-item',
            params: { receiverId: loc.id }
          }
        } else {
          return ''
        }
      } else {
        return ''
      }
    },

    // Generate link data for order info page
    generateOrderInfoPageLink (item) {
      if (item && item.model) {
        return {
          name: 'order-info-overview',
          params: { orderId: item.id }
        }
      } else {
        return ''
      }
    },

    // Generate link data for order messages page
    generateOrderMessagesPageLink (item) {
      if (item && item.model) {
        return {
          name: 'order-info-messages',
          params: { orderId: item.id }
        }
      } else {
        return ''
      }
    },

    // Perform query of orders
    async queryData () {
      this.isDataLoading = true

      try {
        // Build sort
        // NOTE - Currently enabled for single field sort only !!!
        let orderBy = null
        const sortBy = this.dataOptions.sortBy
        const sortDesc = this.dataOptions.sortDesc
        if (sortBy && sortBy.length > 0) {
          const isDesc = (sortDesc && sortDesc.length > 0) ? sortDesc[0] : false
          const prefix = (isDesc) ? '-' : ''
          orderBy = `${prefix}${sortBy[0]}`
        }

        // Build pagination
        const page = this.dataOptions.page || 1
        const limit = this.dataOptions.itemsPerPage
        const skip = (page === 1) ? 0 : (page - 1) * limit

        // Build request input
        const params = {
          skip,
          limit
        }

        // Apply sorting
        if (orderBy) params.order_by = orderBy

        // Apply filters
        if (this.status) params.status = this.status
        if (this.showTestOrders && !this.showLiveOrders) params.test_only = true
        if (this.showLiveOrders && !this.showTestOrders) params.live_only = true

        // Apply search query (for order_number only)
        if (this.search) params['order_number.contains'] = this.search

        // Include associations
        if (this.assocs) params.with = this.assocs

        // Determine endpoint
        const uri = (this.credits) ? 'credits' : 'orders'

        // Retrieve results
        this.orderData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri,
          opts: { params }
        })
      } catch (err) {
        this.dataLoadingError = err.message
      }

      this.isDataLoading = false
    }
  }
}
</script>

<style>
  .order-list-table .info-timezone {
    font-size: 1.25em;
    color: #6a6a6a;
    padding-bottom: 12px;
    cursor:pointer;
  }

  .order-list-table .confirm-code {
    letter-spacing: 2px;
    font-size: 1.2em;
    font-weight: 400;
  }

  .order-list-table .route-name-col-value {
    color: #9c9c9c;
    font-weight: 400;
  }

  /* Disable hover effect on row */
  .order-list-table .theme--dark.v-data-table>.v-data-table__wrapper>table>tbody>tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
    background-color: transparent !important;
  }

  .order-list-table .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
  .order-list-table .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
  .order-list-table .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
    height: 120px;
  }

  .order-list-table .v-data-table a {
    text-decoration: none;
  }
  .order-list-table .v-data-table a:hover {
    text-decoration: underline;
  }
</style>
